(ns hai.ui.components.form.core
  "A nestable data driven form component with Malli validation and two compatible and mutually
  available APIs and usage patterns, one using the render body prop pattern with the API passed in
  as props and the second using Re-Frame events."
  (:require
   [hai.entity.helpers :as entity]
   [hai.ui.components :as c]
   [hai.ui.rf-sugar :refer [<s >e >e! defeffect defevent defevent-fx defsub]]
   [hai.ui.utils.validate :refer [validate]]
   [humbleai.belt.malli.transform.default-value :as default-value]
   [humbleai.belt.malli.transform.strip-extra-keys :as strip-extra-keys]
   [humbleai.belt.malli.transform.translate-keys :as translate-keys]
   [humbleai.belt.vector :refer [remove-elem-at]]
   [lambdaisland.ornament :as o]
   [malli.core :as m]
   [malli.transform :as mt]
   [malli.util :as mu]
   [medley.core :as medley :refer [assoc-some]]
   [reagent.core :as r]))

;; NOTE Form events can be called externally with either just the form ID or with the full form
;; props or within the render body using the function APIs.
;;
;; Auto-namespaced, e.g. `::on-submit`, events are generally considered internal APIs and
;; non-auto-namespaced, e.g. `:form/on-submit`, are generally considered public APIs though if you
;; have reason and know what you're doing they can all be called externally.
;; TODO Collect notes into documentation
;; NOTE Forms support both `:on-change` and `:on-valid-change`

(def FieldForm
  [:map
   [:form/render
    {:optional true
     ::translate-keys/from :render}
    fn?]
   ;; Both forms and fields can be used as children of a collection field
   [:form.field.collection/index
    {:optional true}
    :int]])

(def BaseField
  (mu/merge
   FieldForm
   [:map
    [:optional {:optional true} :boolean]
    [:form.field/attr [:or :keyword :string]]
    [:form.field/form-id :any]
    [:form.field/form-setup-id :uuid]
    [:form.field/schema [:fn entity/->schema]]
    [:form.field/component
     {:optional true
      ::translate-keys/from :component}
     ifn?]
    [:form.field/render
     {:optional true
      ::translate-keys/from :render}
     ifn?]
    [:form.field.label/title
     {:optional true
      ::translate-keys/from :title}
     :string]
    [:form.field/description
     {:optional true
      ::translate-keys/from :description}
     :string]
    [:form.field/placeholder
     {:optional true
      ::translate-keys/from :placeholder}
     :string]
    [:form.field/set-value! ifn?]
    [:form.field/on-change
     {:optional true
      ::translate-keys/from :on-change}
     ifn?]
    [:form.field/on-focus
     {:optional true
      ::translate-keys/from :on-focus}
     ifn?]
    ;; TODO remove optional?
    [:form.field/initial-value {:optional true} :any]
    [:form.field/current-value {:optional true} :any]
    [:form.field/valid-value {:optional true} :any]
    [:form.field/valid? :boolean]
    [:form.field/dirty? :boolean]]))

(def Collection
  (mu/merge
   BaseField
   [:map
    [:form.field.collection/field fn?]
    [:form.field.collection/component
     {:optional true}
     fn?]
    ;; When true (default) each row's `:key` includes its value, so a slot remounts
    ;; whenever its value changes. That's required for mount-once nested-form rows
    ;; (see the `:key` comment in `collection`), but it remounts a plain uncontrolled
    ;; input on every keystroke and throws the cursor out. Field components that reset
    ;; themselves (e.g. `input/field`, keyed on `form-setup-id`) set this `false`.
    [:form.field.collection/key-by-value?
     {:optional true}
     :boolean]
    [:form.field/schema [:fn
                         {:error/message "should be a schema of type `:vector`"}
                         (fn [schema]
                           (and (entity/->schema schema)
                                (= :vector (m/type schema))))]]]))

;; TODO move to input field NS
(def Input BaseField)

;; TODO move to input field NS
(def Select
  (mu/merge
   BaseField
   [:map
    [:form.field.select/options
     {::translate-keys/from :options}
     [:vector :any]]]))

(def Field
  [:or
   Collection
   Select
   Input
   BaseField])

(defn- default-id
  [form]
  (cond
    (and (:form.field/form-id form)
         (:form.field.collection/index form))
    [(:form.field/form-id form)
     (:form.field/attr form)
     (:form.field.collection/index form)]

    (:form.field/form-id form)
    [(:form.field/form-id form)
     (:form.field/attr form)]

    (:initial-value-sub form)
    (str (pr-str (:initial-value-sub form))
         " - "
         (gensym))

    :else
    (str (gensym))))

(def Form
  (mu/merge
   FieldForm
   [:map
    ;; Core attrs
    [:form/id
     {::translate-keys/from :id
      ::default-value/fn default-id}
     :any]
    [:form/setup-id
     {::default-value/fn (fn [_] (random-uuid))}
     :uuid]
    [:form/schema
     {::translate-keys/from-any #{:schema :form.field/schema}}
     [:fn entity/->schema]]
    [:form/fields [:map-of [:or :keyword :string] Field]]

    ;; Form state
    [:form/initial-value-sub
     {:optional true
      ::translate-keys/from :initial-value-sub}
     [:or :keyword vector?]]
    [:form/initial-value
     {:optional true
      ::translate-keys/from-any #{:initial-value :form.field/initial-value}}
     [:or map? :nil]]
    [:form/current-value
     [:or map? :nil]]
    [:form/valid-value [:or map? :nil]]
    [:form/valid? :boolean]
    [:form/dirty? :boolean]

    ;; Form API
    [:form/set-value!
     {:optional true
      ::translate-keys/from :form.field/set-value!}
     fn?]

    ;; Change callbacks
    [:form/on-change
     {:optional true
      ::translate-keys/from :on-change}
     fn?]
    [:form/on-valid-change
     {:optional true
      ::translate-keys/from :on-valid-change}
     fn?]

    ;; Submission callbacks and state
    ;; `on-submit`
    [:form/on-submit
     {:optional true
      ::translate-keys/from :on-submit}
     fn?]
    [:form/user-on-submit
     {:optional true}
     fn?]
    [:form/submitting?
     {:default false}
     :boolean]

    ;; Suppress calling `::setup` again if the form does exist at the time of calling `::setup`
    [:form/suppress-setup-if-done?
     {:default false}
     :boolean]]))

(def transformer
  (mt/transformer
   strip-extra-keys/transformer
   default-value/transformer
   translate-keys/transformer))

(def decode-field-props (m/decoder Field transformer))
(def decode-form-props (m/decoder Form transformer))

(defn ->id
  [?id]
  (or (:form/id ?id)
      (:id ?id)
      ?id))

(defn ->form
  [db ?id]
  (let [id (->id ?id)]
    (get-in db [:forms id])))

(defn ->field
  [db ?id attr]
  (let [id (->id ?id)]
    (get-in db [:forms id :form/fields attr])))

(defn ->field-current-value
  [db ?id attr]
  (get (->field db ?id attr) :form.field/current-value))

(defeffect ::call-on-change
  [{:form/keys [on-change current-value] :as form}]
  (when on-change
    (on-change current-value form)))

(defeffect ::call-on-valid-change
  [{:form/keys [on-valid-change valid-value] :as form}]
  (when on-valid-change
    (on-valid-change valid-value form)))

(defevent-fx ::on-change
  [{:keys [db]} ?id]
  (let [{:form/keys [current-value id fields schema] :as form} (->form db ?id)
        ;; We calculate `:form/current-value` starting from the existing value as the form may have
        ;; been initiated with a value that contained more than just the defined fields, i.e. the
        ;; form fields form a sparse cover of the underlying initial value or entity. Form values
        ;; are always maps.
        current-value (reduce (fn [acc [k v]]
                                ;; `nil` values are removed unless `nil` is a valid
                                ;; value. This is done as we don't return empty
                                ;; strings in fields such as input, we return `nil`.
                                (if (when-not (:form.field/valid? v)
                                      (nil? (:form.field/current-value v)))
                                  (dissoc acc k)
                                  (assoc acc k (:form.field/current-value v))))
                              (or current-value {})
                              fields)
        ok-if-absent true
        valid? (boolean (validate schema current-value ok-if-absent))
        form (-> form
                 (assoc :form/current-value current-value
                        :form/valid? valid?
                        :form/dirty? true)
                 (assoc-some :form/valid-value (when valid? current-value)))]
    (validate Form form)
    (cond-> {:db (assoc-in db [:forms id] form)
             ::call-on-change form}
      valid? (assoc ::call-on-valid-change form))))

(defn- set-field-value-fx
  [db ?id attr proposed-value field-patch]
  (let [{:form/keys [id] :as form} (->form db ?id)
        {:form.field/keys [schema valid-value] :as field} (get-in form [:form/fields attr])
        ok-if-absent true
        valid? (boolean (validate schema proposed-value ok-if-absent))
        valid-value (if valid?
                      proposed-value
                      valid-value)
        field (-> field
                  (assoc :form.field/current-value proposed-value
                         :form.field/valid? valid?
                         :form.field/valid-value valid-value
                         :form.field/dirty? true)
                  (merge field-patch))
        form (assoc-in form [:form/fields attr] field)]
    (validate Field field)
    {:db (assoc-in db [:forms id] form)
     :dispatch [::on-change ?id]
     ::on-field-change {:field field
                        :form form}}))

(defevent-fx :form.field/clear!
  [{:keys [db]} ?id attr]
  ;; `input/field` renders text inputs as uncontrolled (`:default-value`) to preserve cursor
  ;; position while typing, only remounting (and so picking up a fresh, empty value) when its
  ;; `:form.field/form-setup-id` key changes, so we bump it here to force that remount, scoped to
  ;; just this field, on top of what plain `:form.field/set-value!` does. This must happen in the
  ;; very same `:db` update as nulling the value, not as a separate, later-dispatched event: with
  ;; those split across two events, Reagent can render in between, seeing the new key (so it
  ;; remounts the input) but the old, not-yet-cleared value (so the fresh remount just recaptures
  ;; the stale text as its new `:default-value`) -- then the value gets nulled after, but the key
  ;; doesn't change again, so nothing remounts a second time and the stale text sticks permanently.
  (set-field-value-fx db ?id attr nil {:form.field/form-setup-id (random-uuid)}))

(defeffect ::on-field-change
  [{{:form.field/keys [on-change current-value] :as field} :field form :form}]
  (when on-change
    (on-change current-value field form)))

(defevent-fx :form.field/set-value!
  [{:keys [db]} ?id attr proposed-value]
  (set-field-value-fx db ?id attr proposed-value nil))

(defeffect ::on-submit
  [{:form/keys [user-on-submit valid-value]}]
  (when user-on-submit
    (user-on-submit valid-value)))

(defevent-fx :form/on-submit
  [{:keys [db]} ?id]
  ;; TODO document submitting? and :on-submit callback
  (let [{:form/keys [id valid?] :as form} (->form db ?id)]
    (when valid?
      {:db (assoc-in db [:forms id :form/submitting?] true)
       ::on-submit form})))

(defevent :form/submitted
  [db ?id]
  (let [id (->id ?id)]
    (assoc-in db [:forms id :form/submitting?] false)))

(defn instantiate-fields
  [{:form/keys [id initial-value schema setup-id] :as form-props}]
  (let [fields (into {}
                     (map (fn ->field
                            [[attr props sub-schema :as _attr-schema]]
                            (let [decode
                                  (m/decoder sub-schema (mt/string-transformer))

                                  set-value!
                                  (fn set-value!
                                    [dom-value]
                                    ;; TODO Use coercion rather?
                                    (let [proposed-value (decode dom-value)]
                                      ;; NOTE Field values can be set externally.
                                      (>e [:form.field/set-value! form-props attr proposed-value])))

                                  field-initial-value
                                  (let [field-initial-value (get initial-value attr)]
                                    (m/decode sub-schema field-initial-value default-value/transformer))

                                  valid?
                                  (m/validate sub-schema field-initial-value)

                                  valid-value
                                  (when valid? field-initial-value)

                                  props
                                  (-> props
                                      (assoc-some
                                       ;; `form-id` is used when auto-generating the sub-form's
                                       ;; id, see `Form`'s `:form/id` transformer.
                                       :form.field/form-id id
                                       ;; TODO document
                                       :form.field/form-setup-id setup-id
                                       :form.field/attr attr
                                       :form.field/schema (m/form sub-schema)
                                       :form.field/set-value! set-value!
                                       :form.field/initial-value field-initial-value
                                       :form.field/current-value field-initial-value
                                       :form.field/valid-value valid-value
                                       :form.field/valid? valid?
                                       :form.field/dirty? false)
                                      (decode-field-props))]
                              [attr props])))
                     (m/children schema))]
    (assoc form-props :form/fields fields)))

(defevent-fx :form/re-init
  [{:keys [db]} ?id new-initial-value]
  ;; Reuses the form's current (not a fresh) `:form/setup-id`. `:form.field/form-setup-id` is
  ;; derived from this per field and used as the React key for uncontrolled inputs (see
  ;; `input/field`) to force a remount on a genuine reset. `:form/re-init`'s only caller
  ;; (`hai.ui.components.collection.core/::setup`, on every table resolve) uses this just to push
  ;; fresh row data into an already-open form, carrying forward `current-value` for other
  ;; fields — a setup-id bump here has no purpose but would remount (and steal focus from) any
  ;; actively-edited input on the form, e.g. a searchable-select's search box, on every resolve.
  ;; A real reset (`:form/reset`) still generates a fresh id, as intended.
  (let [{:form/keys [initial-props setup-id]} (->form db ?id)]
    {:dispatch [::setup (assoc initial-props
                               :form/initial-value new-initial-value
                               :form/setup-id setup-id)]}))

(defevent-fx :form/reset
  [{:keys [db]} ?id]
  (let [form (->form db ?id)]
    {:dispatch [::setup (assoc form :form/setup-id (random-uuid))]}))

(defsub :forms
  [db]
  (:forms db))

(defsub :form
  [?id
   forms <- :forms]
  (get forms (->id ?id)))

(defsub :form/field
  [?id
   attr
   form <- [:form ?id]]
  (get-in form [:form/fields attr]))

(defsub :form.field/current-value
  [?id
   attr
   field <- [:form/field ?id attr]]
  (get field :form.field/current-value))

(defsub :form.field/dirty?
  [?id
   attr
   field <- [:form/field ?id attr]]
  (get field :form.field/dirty?))

(defn db->current-value
  [db ?table]
  (let [id (->id ?table)]
    (get-in db [:forms id :form/current-value])))

(defsub :form/current-value
  [?id
   form <- [:form ?id]]
  (:form/current-value form))

(defsub :form/valid-value
  [?id
   form <- [:form ?id]]
  (:form/valid-value form))

(defsub :form/submitting?
  [?id
   form <- [:form ?id]]
  (:form/submitting? form))

(defsub :form/dirty?
  [?id
   form <- [:form ?id]]
  (:form/dirty? form))

(defevent ::setup
  [db {:form/keys [id initial-value schema on-submit on-change user-on-submit set-value! suppress-setup-if-done?] :as props}]
  (if (and suppress-setup-if-done?
           (contains? (:forms db) id))
    db
    (let [initial-value (m/decode schema initial-value default-value/transformer)
          valid? (m/validate schema initial-value)
          on-submit (or user-on-submit on-submit)
          form (-> props
                   (assoc
                    :form/initial-props props
                    :form/current-value initial-value
                    :form/initial-value initial-value
                    :form/valid-value (when valid? initial-value)
                    :form/valid? valid?
                    :form/dirty? false
                    ;; This is the internal, in render body, API.
                    :form/on-submit (fn [_e]
                                      ;; NOTE Forms can be submitted externally.
                                      (>e [:form/on-submit props])))
                   (assoc-some
                    ;; We store the user `on-submit` in order to call it, if present, in the
                    ;; `::on-submit` effect
                    :form/user-on-submit on-submit
                    ;; To support using forms as fields
                    ;; TODO sub-form on-change is lost as it becomes set-value! This was removed
                    ;; originally as it created duplicate callbacks but could still be useful.
                    :form/on-change (or (and set-value!
                                             (fn [value form]
                                               (set-value! (with-meta value {:form form})
                                                           form)))
                                        on-change))
                   (instantiate-fields))]
      (validate Form form)
      (update-in db [:forms id] merge form))))

(defn component*
  [props]
  ;; NOTE As the form is set up in an event for both access to the db and to enable re-initialising
  ;; forms externally we use a synchronous event so that field components will only be
  ;; instantiated after the form has been setup as after instantiation `form` will contain
  ;; handlers etc. The alternatives could be to use an asynchronous event and a `(when @form...)`
  ;; guard but that creates extra re-renders. Another alternative would be react hooks.
  ;; TODO untested. NOTE Forms can be re-instantiated externally.
  (>e! [::setup props])
  (let [form (<s [:form props])]
    (fn component*-inner
      [_]
      [(:form/render @form) @form])))

(defn component
  [props]
  (let [{:form/keys [schema] :as props} (decode-form-props props)
        props (-> (merge (decode-form-props (m/properties schema))
                         props))]
    ;; NOTE can be initialised with either a subscription or an explicit initial value.
    ;;
    ;; Explicit `:initial-value` example:
    ;;
    ;; (when-let [user @(<s :user)]
    ;;   [form/component
    ;;    {:initial-value user
    ;;     ...}])
    ;;
    ;; `:initial-value-sub` example:
    ;;
    ;; [form/component
    ;;  {:initial-value-sub :user
    ;;   ...}]
    (if-let [initial-value-sub (:form/initial-value-sub props)]
      (when-let [initial-value @(<s initial-value-sub)]
        ^{:key (hash initial-value)}
        [component* (assoc props :form/initial-value initial-value)])
      [component* props])))

;; Label

(defn label
  [{:form.field.label/keys [title] :as field} & children]
  (into [:label
         ;; TODO for
         ;; {:for id}
         [c/Text {:weight "bold"} title]]
        children))

;; Collection

(defn collection
  [{:as collection
    :form.field/keys [form-id attr set-value! current-value schema]
    :form.field.collection/keys [field component]}
   & children]
  (validate Collection collection)
  (r/with-let [;; A collection field is a vector of field values
               sub-field-schema (entity/->to-one-schema schema)
               current-value-sub (<s [:form.field/current-value form-id attr])
               ;; See below comment
               iteration-key (atom 0)
               ;; See below comment and the schema. Default true keeps the value in the
               ;; row key (needed for mount-once nested-form rows); `list-of`/uncontrolled
               ;; inputs pass false so typing doesn't remount the input each keystroke.
               key-by-value? (get collection :form.field.collection/key-by-value? true)]
    (into [(or component :<>)]
          (map-indexed
           (fn [sub-field-index sub-field-value]
             (let [valid? (m/validate sub-field-schema sub-field-value)
                   sub-field-set-value! (fn sub-field-set-value!
                                          [sub-field-value]
                                          (-> @current-value-sub
                                              (assoc sub-field-index sub-field-value)
                                              (set-value!)))
                   remove-self! (fn remove-self!
                                  []
                                  ;; See below comment
                                  (swap! iteration-key inc)
                                  (-> @current-value-sub
                                      (remove-elem-at sub-field-index)
                                      (set-value!)))
                   sub-field (assoc collection
                                    :form.field/schema sub-field-schema
                                    ;; This is used by forms for their form ID during
                                    ;; instantiation
                                    :form.field.collection/index sub-field-index
                                    :form.field/current-value sub-field-value
                                    ;; This is used by forms for setting up their
                                    ;; initial state
                                    :form.field/initial-value sub-field-value
                                    :form.field/valid-value (when valid? sub-field-value)
                                    :form.field/valid? valid?
                                    :form.field/set-value! sub-field-set-value!
                                    :form.field.collection/remove-self! remove-self!)]
               (into
                ^{:key (hash
                        (cond-> [(:form.field/form-setup-id sub-field)
                                 sub-field-index
                                 ;; React's handling (the correct maintenance of cursor position
                                 ;; specifically) of controlled inputs only works when the value changes
                                 ;; synchronously, as we have Reagent and Re-Frame in the loop, `set-value!`
                                 ;; is async as Reagent renders asynchronously and it fires in an event (so
                                 ;; that we can support changing fields via events). As such we need to be
                                 ;; able to use uncontrolled inputs for when Reagent can't employ it's
                                 ;; workarounds to make it's asynchronous update and render work like
                                 ;; synchronous updates work in React. However Reagent cannot to do this for
                                 ;; library component such as Radix-UI. One option would be to use
                                 ;; Radix-UI's `asChild` prop to supply the rendered component ourselves and
                                 ;; we may want to figure out how to do that for other reasons but as I need
                                 ;; to re-initialise form components as fields anyway I'm using the same
                                 ;; technique of updating the key metadata to force React to re-render the
                                 ;; component. This needs to happen when we remove sub-fields as even though
                                 ;; React will supply the new props to the field it won't re-render the
                                 ;; component and so uncontrolled fields will still use the DOM value they
                                 ;; already had.
                                 @iteration-key]
                          ;; Each sub-field's own nested form (see `component*`) only runs its setup
                          ;; effect once, at mount, so a slot that keeps the same key across a resolve
                          ;; keeps showing whatever it showed at mount regardless of new data flowing
                          ;; through props. `form-setup-id` + `sub-field-index` alone are purely
                          ;; positional, so an insert/remove anywhere but the tail shifts later rows
                          ;; into slots that keep their *old* occupant's key -- and so their old
                          ;; occupant's content -- while the genuinely new slot at that index mounts
                          ;; fresh, showing what's now there. Net effect: stale/duplicated rows until a
                          ;; full page refresh. Including the value itself means a slot only keeps its
                          ;; key (and so its mounted state) when what occupies it hasn't changed.
                          ;;
                          ;; But a slot whose field component resets *itself* (an uncontrolled
                          ;; `input/field`, keyed on `form-setup-id`) must NOT remount just because its
                          ;; value changed -- that is exactly a keystroke, and remounting throws the
                          ;; cursor to the end. Those callers pass `:key-by-value? false`; removal there
                          ;; is instead covered by the `iteration-key` bump above.
                          key-by-value? (conj sub-field-value)))}
                [field sub-field]
                children))))
          current-value)))

(o/defstyled list-of :div
  :flex :flex-col :gap-2
  [:button :w-fit]
  ([field-component
    {:form.field/keys [form-id attr set-value! current-value schema] :as field}
    & children]
   [:<>
    (-> [collection (assoc field
                           :form.field.collection/field field-component
                           ;; `field-component` renders an uncontrolled `input/field` that
                           ;; keys/resets itself. Keep its value out of the row key so typing
                           ;; doesn't remount the input and bounce the cursor to the end.
                           :form.field.collection/key-by-value? false)]
        (into children))
    [c/Button
     {:on-click (fn [_] (set-value! (conj current-value nil)))
      :variant "ghost"}
     "Add +"]]))

(defn checkbox
  [{:form.field/keys [set-value! current-value]}]
  [c/Checkbox
   {:onCheckedChange set-value!
    :defaultChecked current-value}])

(defn field
  [{:form.field/keys [component] :as field}]
  (if component
    [component field]
    (do (cljs.pprint/pprint component)
        [:div "no :form.field/component"])))

(defn render-field
  [{:form.field/keys [render] :as field}]
  (if render
    [render field]
    (do (cljs.pprint/pprint render)
        [:div "no :form.field/render"])))
